import React, { Fragment, useRef, useState } from 'react';
import classNames from 'classnames';
import { Company, FCP, Job } from 'types';

import companyReviewClasses from 'components/companyCard/companyReview.module.css';
import { PopupCloseIcon } from 'components/svg';
import { copyToClipboard, ismobileDevice, openInNewTab } from 'utils';

import classes from './index.module.css';

const SHARE_TO = Object.freeze({
  fb: 'facebook',
  messenger: 'fb_messenger',
  twitter: 'twitter',
  email: 'email',
});

const SHARE_TYPE = Object.freeze({
  job: 'job',
  jobTitleReview: 'jobTitleReview',
  company: 'company',
  careerPath: 'careerPath',
  resume: 'resume',
  // TBD: other types a with message
});

const FB_APP_CLIENT_ID = 508633732650088;

export interface ShareCardProps extends FCP {
  type?: string;
  shareUrl?: string;
  job?: Job;
  companyInfo?: Company;
  closeShareCard(): void;
  closeBtnText?: string;
}

const ShareCard = ({
  closeShareCard,
  children,
  type,
  job,
  companyInfo,
  shareUrl = (window && window.location && window.location.href) || '',
  closeBtnText,
}: ShareCardProps) => {
  const copyPasteShare = useRef(null);
  const [copied, updateCopied] = useState(false);

  const copyLink = (copyPasteShare, shareUrl) => {
    copyPasteShare.current.focus();
    copyPasteShare.current.value = shareUrl;
    copyToClipboard(copyPasteShare);
    updateCopied(true);
  };

  /**
   * @param {Object} props
   * @param {string} props.shareTo where you want to share ex. fb, twitter, email
   * @param {String} props.type which type of link to generate: job, company
   * @param {Object} props.job job object if type is job
   * @param {Object} props.companyInfo companyInfo detail if type if company
   * @param {String} props.shareUrl url to share
   */
  const generateLinkToShare = (shareTo) => {
    // Default Messages
    const message = {
      twitterText:
        'Check out this website Zippia.com. It uses AI technology to help you find the right job.',
      emailSubject: 'Found this site you might like - Zippia',
      emailBody:
        'I found this website I think you might like - www.zippia.com. They help you find jobs you actually want!',
    };
    if (!shareTo) {
      return console.info('ShareTo and type received are invalid');
    }

    switch (type) {
      case SHARE_TYPE.job:
        message.twitterText = `I found this ${job.jobTitle} job that you might like on Zippia.`;
        message.emailSubject = `I found this job you might like - ${job.jobTitle} at ${job.companyName}`;
        message.emailBody = `Here's a job I think you might like: ${job.jobTitle} at ${job.companyName} ${shareUrl}`;
        break;
      case SHARE_TYPE.jobTitleReview:
        message.twitterText = `${job.jobTitle} review on Zippia: "${job.answerText.substring(
          0,
          77
        )}..." `;
        message.emailSubject = `${job.jobTitle} review on Zippia`;
        message.emailBody = `${job.jobTitle} review on Zippia: "${job.answerText}..."  ${shareUrl}`;
        break;
      case SHARE_TYPE.company:
        message.twitterText = `I found this interesting company profile for ${companyInfo.companyNameCPS} on Zippia.com -`;
        message.emailSubject = `Found this company information you might like - ${companyInfo.companyNameCPS}`;
        message.emailBody = `I found this interesting company profile for ${companyInfo.companyNameCPS} on Zippia.com - ${shareUrl}`;
        break;
      case SHARE_TYPE.careerPath:
        message.twitterText = `I found this ${job.jobTitle} career path map that you might find interesting on Zippia.`;
        message.emailSubject = `I found this ${job.jobTitle} career path map that you might find interesting on Zippia`;
        message.emailBody = `I found this ${job.jobTitle} career path map that you might find interesting on Zippia. ${shareUrl}#career-paths`;
        break;
      case SHARE_TYPE.resume:
        message.twitterText = 'I created a new resume here on Zippia. It is awesome.';
        message.emailSubject = 'Look what I found';
        message.emailBody = `I created a new resume here on Zippia. It is awesome. ${shareUrl}`;
        break;
      default:
        break;
    }
    const links = {
      fb: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      fb_messenger: `https://www.facebook.com/dialog/send?app_id=${encodeURIComponent(
        FB_APP_CLIENT_ID
      )}&link=${encodeURIComponent(shareUrl)}&redirect_uri=https://www.facebook.com/`,
      fb_messenger_mobile: `fb-messenger://share?link=${encodeURIComponent(
        shareUrl
      )}&app_id=${encodeURIComponent(FB_APP_CLIENT_ID)}`,
      twitter: `https://twitter.com/share?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(message.twitterText)}`,
      email: `mailto: ?subject=${encodeURIComponent(
        message.emailSubject
      )}&body=${encodeURIComponent(message.emailBody)}`,
    };
    switch (shareTo) {
      case SHARE_TO.fb:
        openInNewTab(links.fb);
        break;
      case SHARE_TO.messenger:
        if (ismobileDevice()) {
          openInNewTab(links.fb_messenger_mobile);
        } else {
          openInNewTab(links.fb_messenger);
        }
        break;
      case SHARE_TO.twitter:
        openInNewTab(links.twitter);
        break;
      case SHARE_TO.email:
        window.open(links.email);
        break;
      default:
        console.info('invalid shareTo:');
        break;
    }
  };

  return (
    <Fragment>
      <div
        className={classNames(classes.shareDialog, companyReviewClasses.shareDialog, 'shareDialog')}
      >
        {closeBtnText ? (
          <div className="share-close-text list-link" onClick={closeShareCard}>
            <span className="arrow-left" />
            {closeBtnText}
          </div>
        ) : (
          <div className="share-close-btn" onClick={closeShareCard}>
            <PopupCloseIcon />
          </div>
        )}
        <div className="shareDialogTitle">SHARE</div>
        <Fragment>{children}</Fragment>
        <ul className="shareDialogButtons">
          <li className="shareDialogButton" onClick={() => generateLinkToShare(SHARE_TO.fb)}>
            <div className="icon icon-icn-fb" />
            <span>Facebook</span>
          </li>
          <li className="shareDialogButton" onClick={() => generateLinkToShare(SHARE_TO.messenger)}>
            <div className="icon icon-facebook-messenger-logo" />
            <span>Messenger</span>
          </li>
          <li className="shareDialogButton" onClick={() => generateLinkToShare(SHARE_TO.twitter)}>
            <div className="icon icon-twitter" />
            <span>Twitter</span>
          </li>
          <li className="shareDialogButton" onClick={() => generateLinkToShare(SHARE_TO.email)}>
            <div className="icon icon-icn-e-mail" />
            <span>Email</span>
          </li>
          <input
            className="position-absolute"
            style={{ left: '-100%', zIndex: '-1' }}
            ref={copyPasteShare}
            type="text"
            name="input"
            contentEditable="true"
          />
          <li
            className="shareDialogButton"
            onClick={() => copyLink(copyPasteShare, shareUrl)}
            title={shareUrl}
          >
            <div className="icon icon-icn-copylink" />
            {copied ? <span>Link Copied!</span> : <span>Copy Link</span>}
          </li>
        </ul>
      </div>
      <div className={`${companyReviewClasses.DLP_overlay} DLP_overlay`} onClick={closeShareCard} />
    </Fragment>
  );
};

ShareCard.defaultProps = {
  shareUrl: undefined,
  children: <></>,
  job: undefined,
  companyInfo: undefined,
  closeBtnText: '',
};

export {
  ShareCard
}

export default ShareCard;
